import { Component, OnInit, ChangeDetectionStrategy, Input, ChangeDetectorRef } from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { RecordOtherDataFormsJSON, RecordOtherDataFormsObject } from 'modules/shared/models/record.models';
import { UpdateRecordOtherDataPayload, RecordOtherDataResponse } from 'store/models/record.models';

import { RecordService } from 'modules/core/services/record.service';
import { SnackbarService } from 'app/services/snackbar.service';

interface FormQuestionKeyList {
  [formKey: string]: Array<string>;
}

interface FormsMetadataList {
  [formName: string]: FormMetadata;
}

interface FormMetadata {
  formTitle: string;
  formPages?: PageTitleObject;
}

interface PageTitleObject {
  [pageIndex: string]: string;
}

@Component({
  selector: 'artemis-customer-other-data',
  templateUrl: './customer-other-data.component.html',
  styleUrls: ['./customer-other-data.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomerOtherDataComponent implements OnInit {

  @Input() editMode: boolean = false;

  @Input() set rawOtherData(rawOtherData: RecordOtherDataResponse) {
    if (rawOtherData  && rawOtherData.results.length) {
      this._rawOtherData = rawOtherData;
      this.parseRawOtherInfo(rawOtherData.results[0].data.surveys);
    }
  }

  get rawOtherData(): RecordOtherDataResponse {
    return this._rawOtherData;
  }

  private unsubscribe$: Subject<void> = new Subject();

  _rawOtherData: any;

  formKeyList: Array<string> = [];
  formQuestionKeyList: FormQuestionKeyList = {};

  otherData: RecordOtherDataFormsObject;
  otherDataMetadata: FormsMetadataList = {};

  openedPanel: Array<number> = [];

  constructor(private cdRef: ChangeDetectorRef,
              private recordService: RecordService,
              private snackbarService: SnackbarService) { }

  ngOnInit() {
  }

  /**
   * Parse raw other data JSON into object
   * @method parseRawOtherInfo
   */
  private parseRawOtherInfo(rawOtherData: string): void {
    const otherDataJSON: RecordOtherDataFormsJSON = JSON.parse(rawOtherData);
    const otherDataObject: RecordOtherDataFormsObject = {};

    Object.keys(otherDataJSON)
      .forEach((otherInfoFormKey: string) => {
        otherDataObject[otherInfoFormKey] = JSON.parse(otherDataJSON[otherInfoFormKey]);
      });

    this.otherData = otherDataObject;
    this.getFormKeyList();
  }

  /**
   * Get an array of key of all forms included in other data
   * @method getFormKeyList
   */
  private getFormKeyList(): void {
    this.formKeyList = [];
    this.openedPanel = [];

    Object.keys(this.otherData).forEach((formKey: string) => {
      this.formKeyList.push(formKey);

      const questionKeys: Array<string> = [];
      Object.keys(this.otherData[formKey]).forEach((otherDataFormQuestionKey: string) => {
        if (otherDataFormQuestionKey === 'metadata') {
          this.getFormMetadata(formKey);
        } else {
          questionKeys.push(otherDataFormQuestionKey);
        }
      });

      questionKeys.sort();
      this.formQuestionKeyList[formKey] = questionKeys;
    });

    this.cdRef.detectChanges();
  }

  /**
   * Get metadata of forms
   * @method getFormMetadata
   */
  private getFormMetadata(formKey: string): void {
    const rawMetadata = JSON.parse(this.otherData[formKey]['metadata']);
    const pageTitlesObject: PageTitleObject = {};

    rawMetadata.formPages.forEach((pageTitle: string) => {
      pageTitlesObject[pageTitle.slice(0, 4)] = pageTitle.substring(4);
    });

    this.otherDataMetadata[formKey] = {
      formTitle: rawMetadata.formTitle,
      formPages: pageTitlesObject
    };
  }

  /**
   * Remove index number in questions
   * @method parseQuestionFormat
   */
  parseQuestionFormat(question: string): string {
    return question.replace(/^(\([0-9]+\))+/, '');
  }

  isArray(object: object): boolean {
    return Array.isArray(object);
  }

  trackByFn(i: number, item: object) {
    return i;
  }
  /**
   * Check whether the answer should be shown as string
   * @method showAnswerAsString
   */
  showAnswerAsString(answer: any): boolean {
    if (typeof answer === 'object') {
      if (Array.isArray(answer)) {
        if (answer.every((item: any) => typeof item === 'string')) {
          return true;
        } else {
          this.getObjectAnswerKeyList(answer);
          return false;
        }
      }
      return false;
    } else {
      return true;
    }

    this.cdRef.detectChanges();
  }

  /**
   * Get an array of keys of fields in the answer if the answer is in object format
   * @method getObjectAnswerKeyList
   */
  getObjectAnswerKeyList(answer: any): Array<string> {
    return Object.keys(answer);
  }

  /**
   * Convert the answer into clean string if the answer is originally in boolean or array of string format
   * @method parseStringAnswerFormat
   */
  parseStringAnswerFormat(answer: boolean | string | Array<string>): string {
    if (typeof answer === 'boolean') {
      return answer ? 'YES' : 'NO';
    } else if (Array.isArray(answer)) {
      return answer.join(',');
    } else {
      return answer;
    }
  }

  /**
   * Expand a form
   * @method openPanel
   */
  openPanel(index: number): void {
    this.openedPanel = [...this.openedPanel, index];
  }

  /**
   * Collapse a form
   * @method closePanel
   */
  closePanel(index: number): void {
    this.openedPanel = this.openedPanel.filter((panelIndex: number) => panelIndex !== index);
  }

  /**
   * Submit updated other data
   * @method submit
   */
  submit(updatedFormKey: string): void {
    const dataToSend: RecordOtherDataFormsJSON = {};

    this.formKeyList.forEach((formKey: string) => {
      dataToSend[formKey] = JSON.stringify(this.otherData[formKey]);
    });

    if (this.editMode) {
      this.recordService
        .updateOtherData(<UpdateRecordOtherDataPayload>{
          otherDataId: this.rawOtherData.results[0].id,
          record: this.rawOtherData.results[0].record,
          customer: this.rawOtherData.results[0].customer,
          data: {
            surveys: JSON.stringify(dataToSend)
          }
        })
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (res: RecordOtherDataResponse) => {
            if (this.otherDataMetadata[updatedFormKey] && this.otherDataMetadata[updatedFormKey].formTitle) {
              this.snackbarService.success(`${this.otherDataMetadata[updatedFormKey].formTitle} updated successfully!`);
            } else {
              this.snackbarService.success(`${updatedFormKey} updated successfully!`);
            }
          },
          (error: any) => {
            if (this.otherDataMetadata[updatedFormKey] && this.otherDataMetadata[updatedFormKey].formTitle) {
              this.snackbarService.error(`Failed to update ${this.otherDataMetadata[updatedFormKey].formTitle}, please try again later.`);
            } else {
              this.snackbarService.error(`Failed to update ${updatedFormKey}, please try again later.`);
            }
          }
        );
    }
  }
}
